import axios from "axios";
import { newApiBaseUrl } from "../../axiosInstance";

/**
 * Customer personal coupons via PGW → CPS.
 * Scoped by JWT userId on the server. `branch` is required.
 * Response: { coupons: [...] }
 */
export const getMyCoupons = (params = {}) => {
  return axios.get(`${newApiBaseUrl}/coupons`, {
    params,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.jwtToken}`,
      "X-Tenant": "hamazon",
    },
  });
};

/**
 * Admin tenant-wide list via PGW → CPS.
 * Optional params: status, code, limit, cursor. `branch` is required.
 * Response: { coupons: [...], nextCursor? }
 */
export const getAdminCoupons = (params = {}) => {
  return axios.get(`${newApiBaseUrl}/admin/coupons`, {
    params,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.jwtToken}`,
      "X-Tenant": "hamazon",
    },
  });
};

/**
 * Admin batch create via PGW → CPS.
 * Caller must pass a UUID v4 idempotencyKey once per submit (reuse only on retry).
 */
export const createCouponsBatch = (body, idempotencyKey) => {
  return axios.post(`${newApiBaseUrl}/admin/coupons/batch`, body, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.jwtToken}`,
      "X-Tenant": "hamazon",
      "Idempotency-Key": idempotencyKey,
    },
    // CPS returns 422 with { created, failed } when every customer failed.
    validateStatus: (status) =>
      (status >= 200 && status < 300) || status === 422,
  });
};

const DEFAULT_COUPON_ERROR = "קוד הקופון אינו תקין";

function extractCouponApiMessage(data) {
  if (!data || typeof data !== "object") return undefined;
  const message =
    data.error ?? data.message ?? data.Message ?? data.ErrorMessage;
  if (typeof message !== "string") return undefined;
  const trimmed = message.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

export const validateCoupon = async (
  couponCode,
  customerId,
  orderSum,
  branch,
) => {
  try {
    const res = await axios.post(
      `${newApiBaseUrl}/coupons/validate`,
      {
        Amount: Number(orderSum).toFixed(2),
        Coupons: [couponCode],
        CustomerId: customerId,
        ...(branch ? { Branch: String(branch) } : {}),
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `bearer ${localStorage.jwtToken}`,
          "X-Tenant": "hamazon",
          "Access-Control-Allow-Origin": "*",
        },
      },
    );
    const originalAmount = Number(res.data.OriginalAmount);
    const finalAmount = Number(res.data.FinalAmount);
    const usedCoupons = res.data.UsedCoupons || [];
    const requested = String(couponCode).trim().toLowerCase();
    const acceptedCode =
      usedCoupons.find(
        (code) => String(code).trim().toLowerCase() === requested,
      ) || null;
    const couponAccepted = Boolean(acceptedCode);

    return {
      Success: couponAccepted,
      Coupon: {
        CouponCode: acceptedCode || couponCode,
        Sum: originalAmount - finalAmount,
      },
      Message: couponAccepted
        ? undefined
        : extractCouponApiMessage(res.data) || DEFAULT_COUPON_ERROR,
    };
  } catch (e) {
    console.error("validateCoupon failed", e);
    return {
      Success: false,
      Message:
        extractCouponApiMessage(e.response?.data) || DEFAULT_COUPON_ERROR,
    };
  }
};
